import type { Project, Task } from "@/lib/project-types";

/**
 * UI-local notification used by independent project hooks in the workspace
 * and shared session sidebar. The Projects API has no subscription endpoint,
 * so this keeps both views in sync after a local create or archive without
 * changing any backend contract.
 *
 * The payload is deliberately the server-confirmed project DTO rather than a
 * bare "refresh" signal. A second hook can apply that known change
 * synchronously, so the new folder is visible before a follow-up GET has had
 * a chance to complete.
 */
export const PROJECTS_CHANGED_EVENT = "l9-projects-changed";
/** Server-confirmed task changes shared between independent task hooks. */
export const TASK_CHANGED_EVENT = "l9-task-changed";

export type ProjectChange =
  | {
      source: "api";
      type: "upsert";
      project: Project;
    }
  | {
      source: "api";
      type: "remove";
      projectId: string;
    };

export function getProjectChange(event: Event): ProjectChange | null {
  if (!(event instanceof CustomEvent)) return null;
  const detail = event.detail;
  if (!detail || typeof detail !== "object") return null;
  if (
    detail.source !== "api" ||
    (detail.type !== "upsert" && detail.type !== "remove")
  ) {
    return null;
  }
  if (detail.type === "upsert" && detail.project && typeof detail.project.id === "string") {
    return detail as ProjectChange;
  }
  if (detail.type === "remove" && typeof detail.projectId === "string") {
    return detail as ProjectChange;
  }
  return null;
}

/**
 * Dispatch a project change. The optional argument preserves compatibility
 * with the older invalidation-only event while new callers can synchronise
 * their local view immediately.
 */
export function notifyProjectsChanged(change?: ProjectChange): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent<ProjectChange | undefined>(PROJECTS_CHANGED_EVENT, {
    detail: change,
  }));
}

export function getTaskChange(event: Event): Task | null {
  if (!(event instanceof CustomEvent)) return null;
  const detail = event.detail;
  if (!detail || typeof detail !== "object") return null;
  const task = detail as Partial<Task>;
  if (typeof task.id !== "string" || typeof task.project_id !== "string") return null;
  return task as Task;
}

/** Notify other task views after a mutation has returned its confirmed DTO. */
export function notifyTaskChanged(task: Task): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent<Task>(TASK_CHANGED_EVENT, { detail: task }));
}
